import {
  BoxGeometry,
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  Quaternion,
  Vector3,
  type Scene,
} from 'three';
import { BuildingMaterial } from '@/shaders/building-material';
import type { Building, BuildingStyle, City } from '@/world/city';
import { AI } from '@/config';
import { clamp01 } from '@/utils/math';

/**
 * Maps a logical building style to the float index uploaded in the `aStyle`
 * instanced attribute. The shader reads this back and switches its facade
 * language on it, so this mapping is the contract between generator and shader:
 *   0 = tower, 1 = slab, 2 = court, 3 = stack, 4 = spire, 5 = terrace.
 */
const STYLE_INDEX: Record<BuildingStyle, number> = {
  tower: 0,
  slab: 1,
  court: 2,
  stack: 3,
  spire: 4,
  terrace: 5,
};

/**
 * Renders every building in the city with a single InstancedMesh — one draw call
 * for the entire skyline. This is the backbone of the "many thousands of buildings
 * stay at 60fps" requirement.
 *
 * Structural data (footprint, position, rotation) lives in the per-instance matrix
 * and is written only when a building is created. The *animated* data — growth and
 * height — lives in cheap per-instance float attributes updated each frame while
 * anything is in motion, then left alone once the skyline is settled.
 */
const _matrix = new Matrix4();
const _pos = new Vector3();
const _quat = new Quaternion();
const _scale = new Vector3();
const _up = new Vector3(0, 1, 0);

export class BuildingField {
  readonly mesh: InstancedMesh;
  readonly material: BuildingMaterial;

  private aHeight: InstancedBufferAttribute;
  private aGrowth: InstancedBufferAttribute;
  private aTone: InstancedBufferAttribute;
  private aSeed: InstancedBufferAttribute;
  private aStyle: InstancedBufferAttribute;
  /** Seed-city height per instance (0 = player-caused); the finale collapses to it. */
  private aSeedH: InstancedBufferAttribute;

  private freeSlots: number[] = [];
  /** slot -> building, for animating in place. */
  private active: (Building | null)[];
  private attrDirty = false;

  constructor(scene: Scene, capacity = 4096) {
    this.active = new Array(capacity).fill(null);

    const geo = new BoxGeometry(1, 1, 1);
    geo.translate(0, 0.5, 0); // anchor base at y=0 so growth rises upward.

    const mk = (n: number) => {
      const a = new InstancedBufferAttribute(new Float32Array(capacity * n), n);
      a.setUsage(DynamicDrawUsage);
      return a;
    };
    this.aHeight = mk(1);
    this.aGrowth = mk(1);
    this.aTone = mk(1);
    this.aSeed = mk(1);
    this.aStyle = mk(1);
    this.aSeedH = mk(1);
    geo.setAttribute('aHeight', this.aHeight);
    geo.setAttribute('aGrowth', this.aGrowth);
    geo.setAttribute('aTone', this.aTone);
    geo.setAttribute('aSeed', this.aSeed);
    geo.setAttribute('aStyle', this.aStyle);
    geo.setAttribute('aSeedH', this.aSeedH);

    this.material = new BuildingMaterial();
    this.mesh = new InstancedMesh(geo, this.material, capacity);
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false; // one big object spanning the whole city.
    this.mesh.count = capacity;

    // Start every slot hidden (degenerate scale) and free.
    for (let i = capacity - 1; i >= 0; i--) {
      this.hideSlot(i);
      this.freeSlots.push(i);
    }
    this.mesh.instanceMatrix.needsUpdate = true;

    scene.add(this.mesh);
  }

  private hideSlot(slot: number): void {
    _matrix.makeScale(0, 0, 0);
    this.mesh.setMatrixAt(slot, _matrix);
    this.aGrowth.setX(slot, 0);
    this.aHeight.setX(slot, 0);
    this.aSeedH.setX(slot, 0);
  }

  private assignSlot(b: Building): void {
    const slot = this.freeSlots.pop();
    if (slot === undefined) {
      // Capacity exhausted — refuse silently rather than crash. In practice the
      // capacity is sized above the city's maximum footprint.
      b.slot = -1;
      return;
    }
    b.slot = slot;
    this.active[slot] = b;

    _pos.set(b.x, 0, b.z);
    _quat.setFromAxisAngle(_up, b.yaw);
    _scale.set(b.width, 1, b.depth); // height handled in the shader via aHeight.
    _matrix.compose(_pos, _quat, _scale);
    this.mesh.setMatrixAt(slot, _matrix);
    this.mesh.instanceMatrix.needsUpdate = true;

    this.aTone.setX(slot, b.tone);
    this.aSeed.setX(slot, b.seed);
    this.aStyle.setX(slot, STYLE_INDEX[b.style]);
    this.aHeight.setX(slot, b.height);
    this.aSeedH.setX(slot, b.seedHeight);
    // Honour any pre-set growth (0 for a freshly born building, 1 for one restored
    // from a save) so loaded cities appear intact instead of re-growing.
    this.aGrowth.setX(slot, b.growth);
    this.attrDirty = true;
  }

  /** Rewrite the instance matrix after a building's footprint/position changed. */
  refresh(b: Building): void {
    if (b.slot < 0) return;
    _pos.set(b.x, 0, b.z);
    _quat.setFromAxisAngle(_up, b.yaw);
    _scale.set(b.width, 1, b.depth);
    _matrix.compose(_pos, _quat, _scale);
    this.mesh.setMatrixAt(b.slot, _matrix);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  private releaseSlot(b: Building): void {
    if (b.slot < 0) return;
    this.hideSlot(b.slot);
    this.active[b.slot] = null;
    this.freeSlots.push(b.slot);
    this.mesh.instanceMatrix.needsUpdate = true;
    this.attrDirty = true;
    b.slot = -1;
  }

  /** Reconcile the render field with the city model and advance animations. */
  update(city: City, dt: number, elapsed: number): void {
    this.material.tick(elapsed);
    const rate = dt / AI.growthDuration;

    // Assign slots to any newly added buildings.
    for (const b of city.buildings) {
      if (b.slot < 0 && !b.dying) this.assignSlot(b);
    }

    // Advance growth / decay for everything with a slot.
    for (let i = city.buildings.length - 1; i >= 0; i--) {
      const b = city.buildings[i];
      if (b.slot < 0) {
        // A building marked to die before it ever rendered: drop it outright so
        // it can't linger as an invisible ghost in the model.
        if (b.dying) city.buildings.splice(i, 1);
        continue;
      }

      if (b.dying) {
        b.growth -= rate * 1.4;
        if (b.growth <= 0) {
          this.releaseSlot(b);
          city.buildings.splice(i, 1);
          continue;
        }
        this.aGrowth.setX(b.slot, clamp01(b.growth));
        this.attrDirty = true;
        continue;
      }

      // Track height changes (e.g. verticality-driven raises) smoothly.
      const curH = this.aHeight.getX(b.slot);
      if (Math.abs(curH - b.height) > 0.01) {
        const nh = curH + (b.height - curH) * Math.min(1, dt * 1.5);
        this.aHeight.setX(b.slot, nh);
        // seedHeight is stamped once and never animates, but refresh it alongside
        // aHeight so the memory attribute can never drift out of sync with a slot.
        this.aSeedH.setX(b.slot, b.seedHeight);
        this.attrDirty = true;
      }

      if (b.growth < 1) {
        b.growth = clamp01(b.growth + rate);
        this.aGrowth.setX(b.slot, b.growth);
        this.attrDirty = true;
      }
    }

    if (this.attrDirty) {
      this.aGrowth.needsUpdate = true;
      this.aHeight.needsUpdate = true;
      this.aTone.needsUpdate = true;
      this.aSeed.needsUpdate = true;
      this.aStyle.needsUpdate = true;
      this.aSeedH.needsUpdate = true;
      this.attrDirty = false;
    }
  }

  /**
   * Drive the finale "memory wave". `m` in [0,1] mixes every instance's rendered
   * height between its built height (0) and its seed height (1), staggered by
   * distance from (centreX, centreZ) — see the shader for the algebra. Render-side
   * only: no Building model value is ever mutated, so m returning to 0 restores the
   * skyline exactly.
   */
  setMemory(m: number, centreX: number, centreZ: number): void {
    const u = this.material.uniforms;
    u.uMemory.value = m;
    u.uMemoryCentre.value.set(centreX, centreZ);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.mesh.removeFromParent();
  }
}
